import { addWorkspaceHeaderIfNeeded, downloadFile } from "@/_helpers/utils";
import type { Pagination } from "@/types";
import { defineStore } from "pinia";
import api from "@/_helpers/api";
import { i18n } from "@/plugins/i18n";
import { axiosInstance } from "@/plugins/https";
import { useSnackbarStore } from "@/_store/snackbar.module";
import { StoreModule } from "@/constants/store.ts";
import {
  type UserSettingsFilterItem,
  useUsersSettingsStore,
} from "@/_store/users-settings/users-settings.module";
import type { DialogDataConfig } from "@/_store/dialogs.module";
import { Service } from "@/constants/cloud-apps";
import { ProxyDirection } from "@/constants/email-proxy";
import type { EmailProxyDomain } from "@/_store/email-security/email-proxy.module";
import { getUserSettingsFilters } from "@/_store/users-settings/adapters";

export enum UserSettingsJoinedVia {
  INDIVIDUALLY = "INDIVIDUALLY",
  ADMIN = "ADMIN",
  GROUP = "GROUP",
  INBOUND_GATEWAY = "INBOUND_GATEWAY",
  CSV = "CSV",
}

export enum UserSettingsAppStatus {
  ACTIVE = "active",
  INACTIVE = "inactive",
}

export interface UserListItemGroup {
  groupId: string;
  name: string;
  service: Service;
}

export interface UserSettingsProtectedApp {
  appName: Service;
  active: boolean;
  alias: string;
}

export interface UsersSettingsFilters {
  searchTerm: string;
  connectedApps?: Service[];
  status: UserSettingsAppStatus | null;
  joinedVia: UserSettingsFilterItem[];
}

export interface UserSettingsListItem {
  userId: string;
  email: string;
  addedIndividually: boolean;
  groups: UserListItemGroup[];
  protectionGrantedTime: number;
  exceptedDateTime: number;
  exceptedBy: string;
  aliases: string[];
  cloudServices: string[];
  exceptedUser: boolean;
  joinedVia: UserSettingsJoinedVia[];
  connectedCloudApplications: UserSettingsProtectedApp[];
}

export type JoinedViaGroups = { [key in Service]?: { total: number; [subkey: string]: number } };

export enum JoinedViaFilterType {
  JOINED_VIA = "joinedVia",
  JOINED_VIA_INBOUND_GATEWAY_DOMAINS = "joinedViaInboundGatewayDomains",
  JOINED_VIA_GROUPS = "joinedViaGroups",
}

export interface JoinedViaFilterParams {
  [JoinedViaFilterType.JOINED_VIA]: string;
  [JoinedViaFilterType.JOINED_VIA_INBOUND_GATEWAY_DOMAINS]: string;
  [JoinedViaFilterType.JOINED_VIA_GROUPS]: string;
}

export interface JoinedViaFilterOptions extends JoinedViaGroups {
  general?: { [key in UserSettingsJoinedVia]?: number };
  inboundGateway?: Record<string, number>;
}

export interface UsersSettingsState {
  allUsers: number;
  pages: number;
  pagination: Pagination;
  users: UserSettingsListItem[];
  showSkeletonLoader: boolean;
  loading: boolean;
  joinedViaFilterOptions: JoinedViaFilterOptions;
  usersTabFilters: UsersSettingsFilters;
}

export const defaultUsersTabFilters = {
  searchTerm: "",
  connectedApps: [],
  joinedVia: [],
  status: null,
};

const defaultUsersState = {
  allUsers: 0,
  pages: 0,
  pagination: {
    page: 0,
    pageSize: 25,
  },
  users: [], // we are caching items to handle case with selection and switching pages
  showSkeletonLoader: true,
  loading: true,
  joinedViaFilterOptions: {},
  usersTabFilters: {
    ...defaultUsersTabFilters,
  },
};

export const useUsersSettingsTabStore = defineStore(StoreModule.USERS_SETTINGS_TAB, {
  state: (): UsersSettingsState => ({ ...defaultUsersState }),
  getters: {
    defaultUsersTabFilters() {
      return defaultUsersTabFilters;
    },
  },
  actions: {
    updateFilters(newFilters: UsersSettingsFilters) {
      this.usersTabFilters = newFilters;
    },
    getUsers() {
      const filters = this.usersTabFilters;

      const denormalizedFilters = getUserSettingsFilters(filters);

      const request = {
        ...api.users(),
        params: {
          ...this.pagination,
          ...denormalizedFilters,
          email: filters.searchTerm,
        },
      };
      this.loading = true;
      this.showSkeletonLoader = true;
      axiosInstance
        .request(request)
        .then((response) => {
          const data = response.data;
          this.users = data.items;
          this.joinedViaFilterOptions = data.groupSummary;
          this.allUsers = data.total;
          this.loading = false;
          this.showSkeletonLoader = false;
        })
        .catch((error) => {
          console.error(error);
          this.loading = false;
          this.showSkeletonLoader = false;
        });
    },
    searchProtectedUsers() {
      const filters = this.usersTabFilters;

      const request = {
        ...api.users(),
        params: {
          searchTerm: filters.searchTerm,
          pagination: { page: 0, pageSize: 10 },
        },
      };
      return axiosInstance.request(request);
    },
    /**
     * This action will search users in cloud services
     * @param email
     * @returns {Promise<AxiosResponse}
     */
    searchUsers(email: string) {
      const request = {
        ...api.searchUsers(email),
      };
      return axiosInstance.request(request);
    },
    /**
     * This action will search already synchronized users
     * @returns {Promise<AxiosResponse}
     */
    searchSyncedUsers() {
      const filters = this.usersTabFilters;

      const request = {
        ...api.users(),
        params: {
          ...this.pagination,
          searchTerm: filters.searchTerm,
        },
      };
      return axiosInstance.request(request);
    },
    applyPaginationChange(payload: Pagination) {
      const pagination = {
        page: payload.page,
        pageSize: payload.pageSize,
      };
      this.pagination = pagination;
      this.getUsers();
    },
    async addUsers({
      users,
      workspaceId,
    }: {
      users: Array<{ email: string; service: Service }>;
      workspaceId?: string;
    }) {
      const snackbarStore = useSnackbarStore();
      const usersSettingsStore = useUsersSettingsStore();
      const request = {
        ...api.addUsers,
        data: users,
      };
      addWorkspaceHeaderIfNeeded(request, workspaceId);
      if (users.length > 30) {
        snackbarStore.addGenericSuccess(i18n.global.t("snackbar.messages.user.usersAreAdding"));
      }

      try {
        await axiosInstance.request(request);
        this.getUsers();
        usersSettingsStore.init();
        snackbarStore.addGenericSuccess(i18n.global.t("snackbar.messages.user.usersUpdated"));
      } catch (error) {
        console.error(error);
      }
    },
    async syncUsers() {
      const snackbarStore = useSnackbarStore();
      const request = {
        ...api.syncUsers,
      };
      try {
        await axiosInstance.request(request);
        snackbarStore.addGenericSuccess(i18n.global.t("snackbar.messages.user.usersSyncStarted"));
      } catch (error) {
        console.error(error);
      }
    },
    async removeUsers(payload: DialogDataConfig) {
      const usersSettingsStore = useUsersSettingsStore();
      const snackbarStore = useSnackbarStore();
      const request = {
        ...api.removeUser({
          userId: payload.item.userId,
          addToExceptionList: payload.item.checkboxValue || false,
        }),
      };

      try {
        await axiosInstance.request(request);
        this.getUsers();
        usersSettingsStore.init();
        snackbarStore.addGenericSuccess(i18n.global.t("snackbar.messages.user.usersUpdated"));
      } catch (error) {
        console.error(error);
      }
    },
    async addAllUsers(payload: DialogDataConfig) {
      const usersSettingsStore = useUsersSettingsStore();
      const snackbarStore = useSnackbarStore();
      const request = {
        ...api.addAllUsersToProtection(payload.item.service),
      };

      try {
        await axiosInstance.request(request);
        usersSettingsStore.init();
        snackbarStore.addGenericSuccess(i18n.global.t("snackbar.messages.user.usersUpdated"));
      } catch (error) {
        console.error(error);
      }
    },
    resetState() {
      this.$reset();
    },
    async addToException(payload: DialogDataConfig) {
      const snackbarStore = useSnackbarStore();
      const { email, userId } = payload.item;
      const request = {
        ...api.addExceptions,
        data: [
          {
            email,
            userId,
            service: "coronet",
          },
        ],
      };
      try {
        await axiosInstance.request(request);
        this.getUsers();
        useUsersSettingsStore().init();
        snackbarStore.addGenericSuccess(i18n.global.t("snackbar.messages.user.usersUpdated"));
      } catch (error) {
        console.error(error);
      }
    },
    async importUsersFromCsv(payload: any) {
      const data = new FormData();
      data.append("file", payload);
      const request = {
        ...api.importUsersViaCsv(data),
      };
      return axiosInstance.request(request);
    },
    async downloadUsersExampleCsv() {
      const request = {
        ...api.getUsersExampleCsv(),
      };

      try {
        const { data, headers } = await axiosInstance.request(request);
        const blob = new Blob([data], { type: headers["content-type"] });
        const fileName = "UsersExample.csv";
        downloadFile(blob, fileName);
      } catch (error) {
        console.error(error);
      }
    },
    async removeException(payload: DialogDataConfig) {
      const snackbarStore = useSnackbarStore();
      const request = {
        ...api.removeException(payload.item.userId),
      };
      try {
        await axiosInstance.request(request);
        this.getUsers();
        useUsersSettingsStore().init();
        snackbarStore.addGenericSuccess(i18n.global.t("snackbar.messages.user.usersUpdated"));
      } catch (e) {
        console.error(e);
      }
    },
    async exportCsv() {
      const snackbarStore = useSnackbarStore();
      const filters = this.usersTabFilters;

      const denormalizedFilters = getUserSettingsFilters(filters);
      const request = {
        ...api.exportUsersCsv,
        params: {
          ...denormalizedFilters,
        },
      };
      try {
        await axiosInstance.request(request);
        this.getUsers();
        snackbarStore.addGenericSuccess(
          i18n.global.t("snackbar.messages.general.exportCsvStarted", {
            pageName: i18n.global.t("activityLogs.title"),
          })
        );
      } catch (e) {
        console.error(e);
      }
    },
    async getProxyDomains(
      direction: ProxyDirection = ProxyDirection.INBOUND
    ): Promise<EmailProxyDomain[]> {
      const request = {
        ...api.emailProxyDomains(),
        params: {
          direction,
        },
      };

      try {
        const { data } = await axiosInstance.request<EmailProxyDomain[]>(request);

        return data;
      } catch (err) {
        console.error(err);
        return [];
      }
    },
    setUsersPagination(pagination: Pagination) {
      this.pagination = pagination;
    },
  },
});
